'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import TalkToBriModalTrigger from '@/components/voice/TalkToBriModalTrigger';
import { TBB_MODAL_OPEN_BODY_CLASS } from '@/components/voice/TalkToBriModal';
import { V4_BRAND } from '@/lib/marketing/messaging-bank';
import './mobile-sticky-voice-bar.css';

/**
 * Routes this bar renders on. Homepage only for now — read by
 * `FloatingVoiceV1Gate` (the shared-const half of the "data-attribute
 * presence check OR shared route list" choice from the A5 brief) so the
 * floating FAB suppresses itself on the same routes/breakpoint instead of
 * doubling up with this bar. Keep in sync if this ever expands past `/`.
 */
export const STICKY_VOICE_BAR_PATHS = ['/'] as const;

/** Matches the mockup's `.sticky-voice` breakpoint exactly (`@media (min-width: 720px) { display: none }`). */
export const STICKY_VOICE_BAR_MEDIA_QUERY = '(max-width: 719.5px)';

/**
 * Set on <body> whenever this bar's breakpoint is active (independent of
 * the modal's own open/close state — the modal locks body scroll, so no one
 * can reach the footer mid-call regardless). Sitewide components mounted on
 * every route — the Footer in particular — key off this to compensate their
 * bottom padding so fixed bar doesn't cover the last inch of content, without
 * the Footer needing to know its own route.
 */
const PAGE_ACTIVE_BODY_ATTR = 'data-sticky-voice-bar-page';

function TalkIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
    </svg>
  );
}

export default function MobileStickyVoiceBar() {
  const [isMobile, setIsMobile] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Breakpoint gate — this is the primary "renders <720px only" mechanism;
  // the CSS file's `@media (min-width: 720px) { display: none }` is
  // defense-in-depth, not the source of truth.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(STICKY_VOICE_BAR_MEDIA_QUERY);
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Hide while the Talk-to-Bri modal is open — read the explicit body class
  // the modal sets, not `document.body.style.overflow` (also 'hidden' during
  // the modal, but not an explicit-enough signal to key rendering off of).
  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    const update = () => setIsModalOpen(document.body.classList.contains(TBB_MODAL_OPEN_BODY_CLASS));
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isMobile) {
      document.body.setAttribute(PAGE_ACTIVE_BODY_ATTR, '');
    } else {
      document.body.removeAttribute(PAGE_ACTIVE_BODY_ATTR);
    }
    return () => {
      document.body.removeAttribute(PAGE_ACTIVE_BODY_ATTR);
    };
  }, [isMobile]);

  const showBar = isMobile && !isModalOpen;

  // TalkToBriModalTrigger (and the modal it owns once opened) stays mounted
  // regardless of `showBar` — only the visible bar markup toggles inside the
  // render prop. Gating the whole `<TalkToBriModalTrigger>` subtree on
  // `showBar` is a trap: the instant the modal opens, TalkToBriModal sets
  // the body class this component watches, `isModalOpen` flips true,
  // `showBar` flips false, and unmounting TalkToBriModalTrigger unmounts the
  // very modal that just opened — closing it in the same tick it opened.
  return (
    <TalkToBriModalTrigger source="sticky_bar">
      {(open) =>
        showBar ? (
          <div className="sticky-voice" data-sticky-voice-bar="" data-testid="mobile-sticky-voice-bar">
            <button
              type="button"
              onClick={open}
              aria-haspopup="dialog"
              className="sticky-voice__talk"
              data-testid="sticky-voice-talk"
            >
              <TalkIcon />
              Talk to Bri
            </button>
            <Link href={V4_BRAND.primaryCtaHref} className="sticky-voice__start" data-testid="sticky-voice-start">
              Get started
            </Link>
          </div>
        ) : null
      }
    </TalkToBriModalTrigger>
  );
}
